/**
 * VAF Shared Auth API (vercel-auth-functions-lower)
 *
 * Generic auth endpoints shared across all products (Spark, Sleepio, etc.).
 * Product-specific activation routes live in their own *ActivationApi modules.
 */
import { envSwitch } from './environmentUtils';
import { logError } from './logError';
import { isVafAcceptableRedirectTarget } from './urlHelpers';

import type { LineOfBusiness } from './constants';

const VAF_BASE = envSwitch({
  // Empty string = relative URL, routed through the webpack dev proxy in src/setupProxy.js.
  local: '',
  dev: 'https://vercel-auth-functions-lower.vercel.app',
  qa: 'https://vercel-auth-functions-lower.vercel.app',
  pr: 'https://vercel-auth-functions-lower.vercel.app',
  stage: 'https://vercel-auth-functions-stage.vercel.app',
  prod: 'https://vercel-auth-functions-prod.vercel.app',
}) ?? '';

// Splits base URL at '?' so an optional Vercel deployment-protection bypass token
// (used on PR previews) is preserved on every constructed URL.
const [VAF_ORIGIN, VAF_QUERY] = VAF_BASE.split('?') as [string, string | undefined];
export const buildVafUrl = (path: string) => (VAF_QUERY ? `${VAF_ORIGIN}${path}?${VAF_QUERY}` : `${VAF_ORIGIN}${path}`);

// ─── requestAuthCode ──────────────────────────────────────────────────────────

interface AuthorizeRawResponse {
  code: string;
}

interface AuthorizeResponse {
  exchangeToken: string;
}

/**
 * Calls POST /api/auth/authorize to generate a short-lived exchange token.
 * Must be called AFTER refreshSession() — access_token must contain status: 'active'.
 * The returned exchange_token expires in 30 seconds.
 */
export const requestAuthCode = async (
  accessToken: string,
  refreshToken: string,
): Promise<AuthorizeResponse> => {
  const url = buildVafUrl('/api/auth/authorize');

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ refresh_token: refreshToken }),
  });

  if (!response.ok) {
    logError('[requestAuthCode]', `failed with status: ${response.status}`, undefined, { status: response.status });
    throw new Error(`Auth authorize failed: ${response.status}`);
  }

  const data: AuthorizeRawResponse = await response.json();
  if (!data.code) {
    throw new Error('Auth authorize returned no token');
  }
  return { exchangeToken: data.code };
};

// ─── activateEmail ────────────────────────────────────────────────────────────

export interface ActivateEmailParams {
  email: string;
  product: string;
  accessCode?: string;
  organizationSlug?: string;
  lob?: LineOfBusiness;
  limbixUserId?: string;
  isDependent?: boolean;
  /** Deep-link/web URI to embed in the magic-link callback URL so the app knows where to
   *  hand the finished session back to. */
  redirectTo?: string;
  phDistinctId?: string;
}

interface ActivateEmailResponse {
  success: boolean;
  limbix_user_id: string;
}

/**
 * Calls POST /api/{product}/{lob}/activate/email to trigger a magic link email.
 *
 * Two flows:
 * - Access code flow: send { email, access_code }
 * - Direct access flow: send { email, organization_slug }
 */
export const activateEmail = async (
  params: ActivateEmailParams,
): Promise<ActivateEmailResponse> => {
  const lob = params.lob ?? 'wellness';
  const url = buildVafUrl(`/api/${params.product}/${lob}/activate/email`);

  const payload: Record<string, string | boolean> = {
    email: params.email,
  };

  if (params.accessCode) {
    payload.access_code = params.accessCode;
  } else if (params.organizationSlug) {
    payload.organization_slug = params.organizationSlug;
  }

  if (params.limbixUserId) {
    payload.limbix_user_id = params.limbixUserId;
  }

  if (params.isDependent) {
    payload.is_dependent = true;
  }

  // Relay only what VAF will accept: an address outside its own list fails the whole request.
  if (params.redirectTo && isVafAcceptableRedirectTarget(params.redirectTo)) {
    payload.redirect_to = params.redirectTo;
  } else if (params.redirectTo) {
    // Dropped, and logged so it isn't silent.
    logError(
      '[activateEmail]',
      'redirectTo rejected by isVafAcceptableRedirectTarget; omitted from request',
      undefined,
      { level: 'warning' },
    );
  }

  if (params.phDistinctId) {
    payload.ph_distinct_id = params.phDistinctId;
  }

  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    logError(
      '[activateEmail]',
      `failed: ${response.status}`,
      undefined,
      { status: response.status, error: body.error, url },
    );
    throw new Error(body.error || `Email activation failed: ${response.status}`);
  }

  return response.json();
};
